<template>
  <div class="modal-overlay" @click="closeModal">
    <div class="modal" @click.stop>
      <div class="modal-header">
        <h3>
          <i class="fas fa-key"></i>
          Modifier le mot de passe
        </h3>
        <button @click="closeModal" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <form @submit.prevent="handleSubmit" class="modal-body">
        <div class="user-info-banner">
          <div class="user-avatar">
            <i class="fas fa-user"></i>
          </div>
          <div class="user-details">
            <span class="user-name">{{ user.prenom }} {{ user.nom }}</span>
            <span class="user-email">{{ user.email }}</span>
          </div>
        </div>

        <div class="form-group">
          <label for="newPassword">Nouveau mot de passe *</label>
          <div class="password-input-wrapper">
            <input
              id="newPassword"
              v-model="form.newPassword"
              :type="showPassword ? 'text' : 'password'"
              class="form-control"
              :class="{ 'is-invalid': errors.newPassword }"
              placeholder="Entrez le nouveau mot de passe"
              required
            />
            <button 
              type="button" 
              @click="showPassword = !showPassword"
              class="toggle-password"
            >
              <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
          <div v-if="errors.newPassword" class="form-error">
            {{ errors.newPassword }}
          </div>
          <div class="password-strength" v-if="form.newPassword">
            <div class="strength-bar">
              <div 
                class="strength-fill" 
                :class="passwordStrengthClass"
                :style="{ width: passwordStrengthPercent + '%' }"
              ></div>
            </div>
            <span class="strength-text" :class="passwordStrengthClass">
              {{ passwordStrengthText }}
            </span>
          </div>
        </div>

        <div class="form-group">
          <label for="confirmPassword">Confirmer le mot de passe *</label>
          <div class="password-input-wrapper">
            <input
              id="confirmPassword"
              v-model="form.confirmPassword"
              :type="showConfirmPassword ? 'text' : 'password'"
              class="form-control"
              :class="{ 'is-invalid': errors.confirmPassword }"
              placeholder="Confirmez le nouveau mot de passe"
              required
            />
            <button 
              type="button" 
              @click="showConfirmPassword = !showConfirmPassword"
              class="toggle-password"
            >
              <i :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
          <div v-if="errors.confirmPassword" class="form-error">
            {{ errors.confirmPassword }}
          </div>
          <div v-if="form.confirmPassword && form.newPassword === form.confirmPassword" class="password-match">
            <i class="fas fa-check-circle"></i>
            Les mots de passe correspondent
          </div>
        </div>

        <div class="password-requirements">
          <h4><i class="fas fa-info-circle"></i> Exigences du mot de passe</h4>
          <ul>
            <li :class="{ 'valid': form.newPassword.length >= 6 }">
              <i :class="form.newPassword.length >= 6 ? 'fas fa-check' : 'fas fa-times'"></i>
              Au moins 6 caractères
            </li>
            <li :class="{ 'valid': /[A-Z]/.test(form.newPassword) }">
              <i :class="/[A-Z]/.test(form.newPassword) ? 'fas fa-check' : 'fas fa-times'"></i>
              Une lettre majuscule
            </li>
            <li :class="{ 'valid': /[0-9]/.test(form.newPassword) }">
              <i :class="/[0-9]/.test(form.newPassword) ? 'fas fa-check' : 'fas fa-times'"></i>
              Un chiffre
            </li>
          </ul>
        </div>

        <div v-if="submitError" class="submit-error">
          <i class="fas fa-exclamation-triangle"></i>
          {{ submitError }}
        </div>

        <div v-if="submitSuccess" class="submit-success">
          <i class="fas fa-check-circle"></i>
          {{ submitSuccess }}
        </div>
      </form>

      <div class="modal-footer">
        <button 
          type="button" 
          @click="closeModal" 
          class="btn btn-secondary"
        >
          Annuler
        </button>
        <button 
          @click="handleSubmit"
          :disabled="loading || !isFormValid"
          class="btn btn-primary"
        >
          <span v-if="loading" class="loading-spinner"></span>
          <i v-else class="fas fa-save"></i>
          {{ loading ? 'Modification...' : 'Modifier le mot de passe' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed } from 'vue'
import api from '@/services/api'

export default {
  name: 'ChangePasswordModal',
  props: {
    user: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'password-changed'],
  setup(props, { emit }) {
    const loading = ref(false)
    const submitError = ref('')
    const submitSuccess = ref('')
    const showPassword = ref(false)
    const showConfirmPassword = ref(false)

    const form = reactive({
      newPassword: '',
      confirmPassword: ''
    })

    const errors = reactive({
      newPassword: '',
      confirmPassword: ''
    })

    const passwordStrength = computed(() => {
      const password = form.newPassword
      let strength = 0
      
      if (password.length >= 6) strength += 25
      if (password.length >= 8) strength += 15
      if (/[A-Z]/.test(password)) strength += 20
      if (/[a-z]/.test(password)) strength += 15
      if (/[0-9]/.test(password)) strength += 15
      if (/[^A-Za-z0-9]/.test(password)) strength += 10
      
      return Math.min(strength, 100)
    })

    const passwordStrengthPercent = computed(() => passwordStrength.value)

    const passwordStrengthClass = computed(() => {
      if (passwordStrength.value < 40) return 'weak'
      if (passwordStrength.value < 70) return 'medium'
      return 'strong'
    })

    const passwordStrengthText = computed(() => {
      if (passwordStrength.value < 40) return 'Faible'
      if (passwordStrength.value < 70) return 'Moyen'
      return 'Fort'
    })

    const isFormValid = computed(() => {
      return form.newPassword.length >= 6 && 
             form.newPassword === form.confirmPassword &&
             /[A-Z]/.test(form.newPassword) &&
             /[0-9]/.test(form.newPassword)
    })

    const validateForm = () => {
      errors.newPassword = ''
      errors.confirmPassword = ''
      let isValid = true

      if (!form.newPassword) {
        errors.newPassword = 'Le mot de passe est requis'
        isValid = false
      } else if (form.newPassword.length < 6) {
        errors.newPassword = 'Le mot de passe doit contenir au moins 6 caractères'
        isValid = false
      } else if (!/[A-Z]/.test(form.newPassword)) {
        errors.newPassword = 'Le mot de passe doit contenir au moins une majuscule'
        isValid = false
      } else if (!/[0-9]/.test(form.newPassword)) {
        errors.newPassword = 'Le mot de passe doit contenir au moins un chiffre'
        isValid = false
      }

      if (!form.confirmPassword) {
        errors.confirmPassword = 'Veuillez confirmer le mot de passe'
        isValid = false
      } else if (form.newPassword !== form.confirmPassword) {
        errors.confirmPassword = 'Les mots de passe ne correspondent pas'
        isValid = false
      }

      return isValid
    }

    const handleSubmit = async () => {
      submitError.value = ''
      submitSuccess.value = ''

      if (!validateForm()) {
        return
      }

      loading.value = true

      try {
        await api.put(`/users/${props.user._id}/change-password`, {
          newPassword: form.newPassword
        })

        submitSuccess.value = 'Mot de passe modifié avec succès!'
        
        setTimeout(() => {
          emit('password-changed')
        }, 1500)
      } catch (error) {
        submitError.value = error.response?.data?.message || 'Erreur lors de la modification du mot de passe'
      } finally {
        loading.value = false
      }
    }

    const closeModal = () => {
      emit('close')
    }

    return {
      form,
      errors,
      loading,
      submitError,
      submitSuccess,
      showPassword,
      showConfirmPassword,
      passwordStrengthPercent,
      passwordStrengthClass,
      passwordStrengthText,
      isFormValid,
      handleSubmit,
      closeModal
    }
  }
}
</script>

<style scoped>
.modal {
  max-width: 500px;
  width: 100%;
}

.user-info-banner {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 0.75rem;
  margin-bottom: 1.5rem;
}

.user-avatar {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--secondary-blue) 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
}

.user-details {
  display: flex;
  flex-direction: column;
}

.user-name {
  font-weight: 600;
  color: var(--dark-gray);
  font-size: 1rem;
}

.user-email {
  color: var(--medium-gray);
  font-size: 0.875rem;
}

.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-wrapper .form-control {
  padding-right: 3rem;
}

.toggle-password {
  position: absolute;
  right: 1rem;
  background: none;
  border: none;
  color: var(--medium-gray);
  cursor: pointer;
  padding: 0.25rem;
  transition: color 0.2s;
}

.toggle-password:hover {
  color: var(--primary-blue);
}

.password-strength {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.strength-bar {
  flex: 1;
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
}

.strength-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease, background-color 0.3s ease;
}

.strength-fill.weak {
  background: #dc3545;
}

.strength-fill.medium {
  background: #ffc107;
}

.strength-fill.strong {
  background: #28a745;
}

.strength-text {
  font-size: 0.75rem;
  font-weight: 600;
  min-width: 50px;
}

.strength-text.weak {
  color: #dc3545;
}

.strength-text.medium {
  color: #ffc107;
}

.strength-text.strong {
  color: #28a745;
}

.password-match {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #28a745;
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

.password-requirements {
  background: #f8f9fa;
  border-radius: 0.75rem;
  padding: 1rem;
  margin-top: 1rem;
}

.password-requirements h4 {
  font-size: 0.875rem;
  color: var(--dark-gray);
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.password-requirements ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.password-requirements li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: var(--medium-gray);
  padding: 0.25rem 0;
  transition: color 0.2s;
}

.password-requirements li i {
  width: 16px;
  text-align: center;
}

.password-requirements li.valid {
  color: #28a745;
}

.password-requirements li.valid i {
  color: #28a745;
}

.password-requirements li:not(.valid) i {
  color: #dc3545;
}

.submit-error {
  background-color: rgba(239, 68, 68, 0.1);
  color: #dc3545;
  padding: 0.75rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  margin-top: 1rem;
}

.submit-success {
  background-color: rgba(40, 167, 69, 0.1);
  color: #28a745;
  padding: 0.75rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  margin-top: 1rem;
}

.modal-header h3 {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.modal-header h3 i {
  color: var(--primary-blue);
}

@media (max-width: 768px) {
  .modal {
    margin: 1rem;
    max-width: none;
  }
}
</style>
